import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import App from './App.tsx'
import './styles/globals.css'
import { FirebaseAuthProvider } from "./hooks/use-firebase-auth";
import { initSentry } from './lib/sentry';
import { queryClient } from './lib/queryClient';
import { SentryContextSync } from './components/monitoring/SentryContextSync';

initSentry();

// Global stale-chunk recovery. After a deploy, a user still holding the old
// index.html requests old hashed chunk filenames that now 404 — the dynamic
// import throws "Importing a module script failed" / "Failed to fetch
// dynamically imported module". App.tsx's lazy loader only catches ROUTE
// chunks; this catches every OTHER dynamic import (jszip, notifications, etc.)
// that surfaced as Sentry #2E and as "failed to load notifications" toasts.
// One reload swaps in the fresh index.html. sessionStorage guard (shared key
// with App.tsx, cleared on any successful lazy load) prevents a reload loop.
if (typeof window !== 'undefined') {
  const CHUNK_ERROR_RE =
    /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|not a valid JavaScript MIME type/i;
  // Pages where a reload would throw away unsaved work (a course being edited,
  // an exam being built, files mid-upload). A deploy while an instructor was
  // editing reloaded the editor and lost their changes (2026-10-14). There we
  // say a new version is out and let them save first.
  const EDITING_PATH_RE = /^\/instructor\/(courses\/[^/]+\/(edit|materials)|courses\/create|exams\/)/;
  const reloadOnceForStaleChunk = (message: string): boolean => {
    if (!CHUNK_ERROR_RE.test(message)) return false;
    if (EDITING_PATH_RE.test(window.location.pathname)) {
      if (!document.getElementById('drx-new-version')) {
        const note = document.createElement('div');
        note.id = 'drx-new-version';
        note.setAttribute('role', 'alert');
        note.textContent = 'A new version of DanceRealmX is available. Save your work, then refresh the page.';
        note.style.cssText = 'position:fixed;bottom:16px;left:16px;right:16px;max-width:520px;margin:auto;z-index:9999;padding:12px 16px;border-radius:8px;background:#0e7490;color:#fff;font:14px system-ui;box-shadow:0 4px 16px rgba(0,0,0,.4)';
        document.body.appendChild(note);
      }
      return false;
    }
    if (sessionStorage.getItem('drx:lazy-reload-attempted') === 'true') return false;
    sessionStorage.setItem('drx:lazy-reload-attempted', 'true');
    window.location.reload();
    return true;
  };
  // Vite fires this for a failed <link rel=modulepreload>/dynamic import.
  window.addEventListener('vite:preloadError', (event) => {
    const message =
      (event as unknown as { payload?: { message?: string } })?.payload?.message ??
      'Importing a module script failed';
    if (reloadOnceForStaleChunk(message)) event.preventDefault();
  });
  // Un-awaited dynamic imports reject here (e.g. import('jszip')).
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? '');
    reloadOnceForStaleChunk(message);
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} themes={['light', 'dark']}>
      <FirebaseAuthProvider>
        <SentryContextSync />
        <App />
      </FirebaseAuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);
